import { useMemo } from "react"
import { NudgeFragment, QuestionnaireFragment } from "../generated/graphql"

interface useTechniqueSearchProps {
  searchQuery: string
  nudges: NudgeFragment[]
  questionnaires: QuestionnaireFragment[]
}

const normalize = (text: string) =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")

const tokenize = (text: string) => normalize(text).split(" ").filter(Boolean)

const getNudgeName = (nudge: NudgeFragment) =>
  nudge.technique.replace(" (Specify)", "").trim()

// Computes a match score between a name and a search query
const getMatchScore = (name: string, query: string): number | null => {
  if (!query) {
    return 0
  }

  const normalizedName = normalize(name)
  if (!normalizedName) {
    return null
  }

  const queryTokens = tokenize(query)
  const nameTokens = tokenize(name)

  if (normalizedName === query) {
    return 0
  }

  if (normalizedName.startsWith(query)) {
    return 1
  }

  const startsWithAllTokens = queryTokens.every((queryToken) =>
    nameTokens.some((nameToken) => nameToken.startsWith(queryToken))
  )
  if (startsWithAllTokens) {
    return 2
  }

  const includesAllTokens = queryTokens.every((queryToken) =>
    normalizedName.includes(queryToken)
  )
  if (includesAllTokens) {
    return 3
  }

  return null
}

// Scores, filters, sorts, and unwraps items
const rankMatches = <T>(
  items: T[],
  query: string,
  getName: (item: T) => string
): T[] => {
  return (
    items
      // Score each item
      .map((item, index) => ({
        item,
        index,
        score: getMatchScore(getName(item), query),
      }))
      // Remove non-matches
      .filter(
        (entry): entry is { item: T; index: number; score: number } =>
          entry.score !== null
      )
      // Order by relevance
      .sort((a, b) => {
        if (a.score !== b.score) {
          return a.score - b.score
        }

        return a.index - b.index
      })
      // Return ranked items
      .map((entry) => entry.item)
  )
}

/**
 * Filters and ranks techniques and assessments based on a normalized search query.
 * Returns matched nudges grouped by domain and matched questionnaires ordered by relevance.
 */
export default function useTechniqueSearch({
  searchQuery,
  nudges,
  questionnaires,
}: useTechniqueSearchProps) {
  const normalizedQuery = normalize(searchQuery)

  return useMemo(() => {
    const matchingNudges = rankMatches(nudges, normalizedQuery, getNudgeName)

    const filteredNudgesByDomain = new Map<string, NudgeFragment[]>()
    matchingNudges.forEach((nudge) => {
      const domain = filteredNudgesByDomain.get(nudge.domain) ?? []
      domain.push(nudge)
      filteredNudgesByDomain.set(nudge.domain, domain)
    })

    const filteredQuestionnaires = rankMatches(
      questionnaires,
      normalizedQuery,
      (q) => q.name
    )

    return { filteredNudgesByDomain, filteredQuestionnaires }
  }, [nudges, questionnaires, normalizedQuery])
}
